import { Fragment, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@ui/Button";
import { useMultiStepForm } from "@/hooks";
import { addToDataGrid } from "@/utils/data";
import { getFileDisplayName, handleFileDownload } from "@/utils/download";
import { Step1 } from "@/components/FormSteps";
import { DataGrid, type DataGridItem } from "./DataGrid";
import SummaryStep from "./SummaryStep";

export const SummaryStep1: FC = () => {
  const { form } = useMultiStepForm();
  const { t } = useTranslation();
  const step1 = form.getValues().step1;

  const excel = step1.excelProfile as File | string | undefined;

  const data: DataGridItem[] = [
    ...addToDataGrid(
      "predefined-profile",
      t("summary.predefinedProfile"),
      step1.predefinedProfile ? t(`step1.profile_subForm.q1_${step1.predefinedProfile}`) : "-",
      step1.energyProfileMethod === "PREDEFINED_PROFILE",
    ),

    ...addToDataGrid(
      "consumption",
      step1.consumption === "MONTHLY"
        ? t("summary.monthlyConsumption")
        : t("summary.yearlyConsumption"),
      step1.consumption ? `${step1.consumption} ${t("MWh")}` : "-",
      step1.energyProfileMethod === "PREDEFINED_PROFILE",
    ),

    ...addToDataGrid(
      "table-data",
      t("summary.table"),
      <>
        {step1.tableData && step1.tableData.length > 0 ? (
          <div className="grid w-fit grid-cols-[auto_auto] gap-x-4">
            {step1.tableData.map((consumption, index) => (
              <Fragment key={index}>
                <span className="mx-auto font-extralight">{`${(index + 1).toString()}:`}</span>
                <span>{consumption ? `${consumption} ${t("MWh")}` : "-"}</span>
              </Fragment>
            ))}
          </div>
        ) : (
          "-"
        )}
      </>,
      step1.energyProfileMethod === "TABLE_INPUT",
    ),

    ...addToDataGrid(
      "excel-file",
      t("summary.excelFile"),
      excel ? (
        <Button
          onClick={() => {
            handleFileDownload(getFileDisplayName(excel), t, excel);
          }}
          variant="link"
          className="text-foreground h-fit p-0 text-base"
        >
          {getFileDisplayName(excel)}
        </Button>
      ) : (
        "-"
      ),
      step1.energyProfileMethod === "FILE_INPUT",
    ),
  ];

  return (
    <SummaryStep
      stepTitle="step1"
      stepKey="step1"
      cardTitle={`1. ${t("stepper.step1")} (${t("selectingPredefinedProfile")})`}
      cardContent={<DataGrid items={data} dataTest="summary-step1-data-grid" />}
      formComponent={Step1}
    />
  );
};
